import { useTranslate } from '@bynder/localization';
import { Spinner, Tabs } from '@bynder/design-system';
import { useActiveSection } from '../../../../queries/guides';
import {
  generateDuplicatedSection,
  useSectionMutations,
} from '../../../../mutations/sections';
import { ModalType } from '../../../../types';
import { useGuideStore } from '../../../../stores/guideStore';
import { useInspectorStore } from '../../../../stores/inspectorStore';
import { useModalStore } from '../../../../stores/modalStore';
import { InspectorSetting } from '../../../../constants/guides';
import {
  IconArrowDown,
  IconArrowUp,
  IconCopy,
  IconDelete,
} from '@bynder/icons';
import { useIsEntityChanging } from '../../../../stores/socketsStore';

const SectionSettingsFooter = () => {
  const { translate } = useTranslate();
  const openModal = useModalStore.use.openModal();
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);

  const {
    activeSection,
    sectionCount,
    sectionIndex,
    previousSectionId,
    nextSectionId,
  } = useActiveSection();
  const setActiveSectionId = useGuideStore(s => s.setActiveSectionId);
  const isEntityChanging = useIsEntityChanging(activeGuideId);
  const { deleteSection, moveSection, duplicateSection } =
    useSectionMutations();

  const handleDeleteSectionClick = () => {
    const handleDeleteSection = () => {
      deleteSection.mutate({ sectionId: activeSection.id });
      setActiveInspectorSetting(InspectorSetting.SECTION_VIEW);
      if (sectionIndex === 0) {
        setActiveSectionId(nextSectionId);
      } else {
        setActiveSectionId(previousSectionId);
      }
    };
    const modalProps = {
      headerText: translate('DELETE_SECTION_MODAL.HEADER_TEXT'),
      contentText: translate('DELETE_SECTION_MODAL.CONTENT_TEXT'),
    };
    openModal({
      modalType: ModalType.DELETE_MODAL,
      modalOnSubmit: handleDeleteSection,
      modalProps,
    });
  };

  const handleMoveSection = (position: number) => {
    moveSection.mutate({
      sectionId: activeSection.id,
      sectionIndex,
      to: {
        sectionPosition: position,
      },
    });
  };

  const handleDuplicateSection = () => {
    const { widgetMutations, duplicatedSection, textBlockMutations } =
      generateDuplicatedSection(activeGuideId, activePageId, activeSection.id);
    duplicateSection.mutate({
      sectionId: activeSection.id,
      sectionIndex,
      duplicatedSection,
      widgetMutations,
      textBlockMutations,
    });
  };

  const isFirstSection = sectionIndex === 0;
  const isLastSection = sectionIndex === sectionCount - 1;

  return (
    <Tabs variant="clean">
      <Tabs.Tab
        label={translate('SECTION_MENU.DUPLICATE_TOOLTIP_CONTENT_ENABLED')}
        isLabelHidden
        isDisabled={isEntityChanging}
        icon={isEntityChanging ? <Spinner /> : <IconCopy />}
        isActive={false}
        onClick={handleDuplicateSection}
      />
      <Tabs.Tab
        data-testid="moveSectionUpButton"
        label={translate('SECTION_MENU.MOVE_UP_TOOLTIP_CONTENT')}
        isLabelHidden
        isDisabled={isFirstSection}
        icon={<IconArrowUp />}
        isActive={false}
        onClick={() => handleMoveSection(sectionIndex - 1)}
      />
      <Tabs.Tab
        data-testid="moveSectionDownButton"
        label={translate('SECTION_MENU.MOVE_DOWN_TOOLTIP_CONTENT')}
        isLabelHidden
        isDisabled={isLastSection}
        icon={<IconArrowDown />}
        isActive={false}
        onClick={() => handleMoveSection(sectionIndex + 1)}
      />
      <Tabs.Tab
        data-testid="deleteSectionButton"
        label={translate('SECTION_MENU.DELETE_SECTION_TOOLTIP_CONTENT')}
        isLabelHidden
        isDisabled={sectionCount === 1}
        icon={<IconDelete />}
        isActive={false}
        onClick={handleDeleteSectionClick}
      />
    </Tabs>
  );
};

export default SectionSettingsFooter;
